import dayjs from 'dayjs'
import querystring from 'querystring'
import { NeedsQueryParam } from 'src/models/Needs'
import { NeedsUser, NeedsUsers, User } from 'src/models/User'
import { filterUndefined } from 'src/utils/object'
import useSWR, { SWRConfiguration, mutate } from 'swr'
import { ApiClient, fetcher } from './client'
import {
  COMPANY_SEARCH_CONDITIONS,
  GET_NEEDS,
  GET_NEEDS_CSV,
  POST_INTERESTED,
  companySearchConditionsId,
  getProfile,
} from './endpoints'
import {
  CompanySearchConditionsData,
  NeedsCompanies,
  NeedsCompany,
  PostInterestedRequest,
  PostInterestedResponse,
} from './types/needs'

const needsFetcher = async (url: string) => {
  const data: NeedsCompanies = await fetcher(url)
  return {
    items: data?.items.map((item) => toNeedsUser(item)) ?? [],
    totalCount: data?.totalCount ?? 0,
    totalPages: data?.totalPages ?? 0,
    onePageDisplayItems: data?.onePageDisplayItems ?? 15,
  }
}
export const useNeeds = (
  query: NeedsQueryParam = {},
  page: number = 1,
  config?: Partial<SWRConfiguration>,
) =>
  useSWR<NeedsUsers>(
    `${GET_NEEDS}?buy=${page}${toNeedsQuery(query)}`,
    needsFetcher,
    config,
  )

export const getNeedsCsv = async (
  query: NeedsQueryParam = {},
  page: number = 1,
) => {
  return await fetcher(`${GET_NEEDS_CSV}?buy=${page}${toNeedsQuery(query)}`)
}

export const createCompanySearchConditions = async (
  title: string,
  params: string,
) => {
  const body = {
    title: title,
    params: params,
  }
  const res = await ApiClient.post(COMPANY_SEARCH_CONDITIONS, body)
  return res
}

const companySearchConditionsFetcher = async (
  url: string,
): Promise<CompanySearchConditionsData[]> => {
  const res = await fetcher(url)
  return res
}

export const useCompanySearchConditions = (
  config: Partial<SWRConfiguration> = {},
) =>
  useSWR<CompanySearchConditionsData[]>(
    COMPANY_SEARCH_CONDITIONS,
    companySearchConditionsFetcher,
    config,
  )

export const deleteCompanySearchConditions = async (id: number) => {
  const res = await ApiClient.delete(companySearchConditionsId(id))
  return res
}

const toNeedsUser = (data: NeedsCompany): NeedsUser => {
  // typeOfBusinessが文字列の場合は配列に変換、配列の場合はそのまま使用
  const typeOfBusinessArray = Array.isArray(data.typeOfBusiness)
    ? data.typeOfBusiness
    : data.typeOfBusiness
    ? [data.typeOfBusiness]
    : null

  return {
    id: data.id,
    roomUrl: data.roomUrl,
    isActive: data.isActive,
    isNew: data.isNew,
    isCertified: data.isCertified,
    name: data.adminName || '',
    companyName: data.name || '',
    position: data.position || '',
    capital: data.capitalText || '',
    businessType: typeOfBusinessArray?.join('、') || '',
    businessTypes: typeOfBusinessArray || [],
    foundedOn: data.foundedAt ? `${dayjs(data.foundedAt).year()}` : undefined,
    imageSrc:
      typeof data.imageUrl === 'string'
        ? data.imageUrl
        : data.imageUrl?.medium?.url || data.imageUrl?.url,
    numberOfEmployees: data.employeeNumberText,
    prefecture: data.prefecture,
    storyUrl: data.storyUrl,
    hpUrl: data.hpUrl,
    fbUrl: data.fbUrl,
    kept: data.kept,
    messageAvailable: data.messageAvailable,
    adminAges: data.adminAges,
    matchingStatus: data.matchingStatus,
    feedbackScore: data.feedbackScore,
    feedbackCount: data.feedbackCount,
  }
}

export const toNeedsQuery = (param: NeedsQueryParam) => {
  const query = querystring
    .stringify(
      filterUndefined({
        type_of_business_id: param.type_of_business_id,
        employee_se: param.employee_se,
        needs: param.needs,
        solutions: param.solutions,
        prefecture_id_se: param.prefecture_id_se,
        position: param.position,
        capital: param.capital,
        is_listed_company: param.is_listed_company,
        founded_at_se: param.founded_at_se,
        business_object_se: param.business_object_se,
        admin_age_se: param.admin_age_se,
        is_new: param.is_new === '1' ? '1' : undefined,
        no_message: param.no_message === '1' ? '1' : undefined,
        refuse_message: param.refuse_message === '1' ? '1' : undefined,
        needs_key: param.needs_key,
        role_for_analysis: param.role_for_analysis,
        min_created_date: param.min_created_date,
        max_created_date: param.max_created_date,
        min_last_sign_in_at: param.min_last_sign_in_at,
        max_last_sign_in_at: param.max_last_sign_in_at,
      }),
    )
    .replaceAll('%7C', '|')
  return query ? `&${query}` : ''
}

export const toNeedsQueryUrl = (param: NeedsQueryParam) => {
  const query = querystring
    .stringify(
      filterUndefined({
        type_of_business_id: param.type_of_business_id,
        employee_se: param.employee_se,
        needs: param.needs,
        solutions: param.solutions,
        prefecture_id_se: param.prefecture_id_se,
        position: param.position,
        capital: param.capital,
        is_listed_company: param.is_listed_company,
        founded_at_se: param.founded_at_se,
        business_object_se: param.business_object_se,
        admin_age_se: param.admin_age_se,
        is_new: param.is_new,
        no_message: param.no_message,
        refuse_message: param.refuse_message,
        needs_key: param.needs_key,
        role_for_analysis: param.role_for_analysis,
        min_created_date: param.min_created_date,
        max_created_date: param.max_created_date,
        min_last_sign_in_at: param.min_last_sign_in_at,
        max_last_sign_in_at: param.max_last_sign_in_at,
        company_id: param.company_id,
      }),
    )
    .replaceAll('%7C', '|')
  return query ? `&${query}` : ''
}

export const postInterested = async (
  userId: number,
  requestReason?: string,
  detail?: string,
) => {
  const body: PostInterestedRequest = {
    request_box: {
      reply_company_id: userId,
      request_reason: requestReason,
      detail: detail,
    },
  }
  const res = await ApiClient.post<PostInterestedResponse>(
    POST_INTERESTED,
    body,
  )
  if (!res.error && res.data)
    mutate(
      getProfile(userId),
      targetCompanyOptimisticMutator(res.data.result),
      false,
    )
  return res
}

const targetCompanyOptimisticMutator =
  (status: User['company']['matchingStatus']) => (data: User) => {
    return {
      ...data,
      company: {
        ...data.company,
        matchingStatus: status,
      },
    }
  }
